import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from '../HATVDashboard.module.scss';
import { useTranslation } from 'react-i18next';
import { useGetMyAwardApprovalQuery, useGetUsersListByNativeIDsQuery } from '../../../../redux/services/hatvAwardApprovalAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMonthOptions } from '../../../Context/MonthYearOptionsContext';
import AwardApproval from '../AwardApproval/AwardApproval';
import CalendarDropdown from '../CalendarDropdown/CalendarDropdown';
import HATVBulkActionBar from '../HATVBulkActionBar/HATVBulkActionBar';
import Icon from '../../../common/Icon/Icon';
import useClickOutside from '../../../CustomHooks/useClickOutside';
import { getSelectedBulkOption } from '../hatvDashboardHelpers';
import ViewRoutingModal from '../../ViewRoutingModal/ViewRoutingModal';
import Modal from '../../../common/Modal/Modal';
import HATVApprovalActionModal from '../../HATVApprovalAction/HATVApprovalActionModal';
import { HATV_APPROVAL_ACTION } from '../../../../constants/dataConstants';
import ToastMessage from '../../../common/ToastMessage/ToastMessage';
import { toastMessageType } from '../../../../types/types';

interface HATVAwardApprovalSectionProps {
  isActive: boolean;
}

const HATVAwardApprovalSection: React.FC<HATVAwardApprovalSectionProps> = ({ isActive }) => {
  const { t } = useTranslation();
  const { monthYearList, selectedYearMonth, setSelectedYearMonth, isLoading, isError } =
    useMonthOptions();

  const [selectedRows, setSelectedRows] = useState<TableRowData[]>([]);
  const [selectedBulkAction, setSelectedBulkAction] = useState<string | null>(null);
  const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
  const [isRoutingModalOpen, setIsRoutingModalOpen] = useState(false);
  const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
  const [toastConfig, setToastConfig] = useState<{
    message: string;
    type: toastMessageType;
    show: boolean;
  } | null>(null);

  const bulkActionsRef = useRef<HTMLDivElement>(null);

  const handleSelectedCode = (value: string) => {
    setSelectedYearMonth(value);
  };

  const {
    data: awardApprovalData,
    isLoading: isAwardApprovalLoading,
    isFetching: isAwardApprovalFetching,
    isError: isAwardApprovalError,
    refetch: refetchAwardApproval,
  } = useAwardApprovalData({
    closingMonth: selectedYearMonth ?? '',
  });

  const handleStatusUpdate = useCallback(() => {
    refetchAwardApproval();
  }, [refetchAwardApproval]);

  const setShowToast = (show: boolean) =>
    setToastConfig((prev) => (prev ? { ...prev, show } : null));

  const handleAPIStatus = (status: string) => {
    if (status === 'success') {
      setToastConfig({
        show: true,
        message: t('successfully_updated_award_status'),
        type: 'success',
      });
    } else if (status === 'fail') {
      setToastConfig({
        show: true,
        message: t('failed_to_update_award_status'),
        type: 'error',
      });
    }
    handleStatusUpdate();
  };

  const selectedItems = selectedRows.length;
  const selectedOptionKey = useMemo(
    () => getSelectedBulkOption(selectedBulkAction),
    [selectedBulkAction]
  );

  const isInitialLoading = isLoading || !selectedYearMonth || isAwardApprovalLoading;

  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    if (bulkActionsRef.current) {
      const rect = bulkActionsRef.current.getBoundingClientRect();
      setMenuCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, []);

  useEffect(() => {
    if (isBulkActionsOpen) {
      updateMenuPosition();
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
    }
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isBulkActionsOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (bulkActionsRef.current && bulkActionsRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      setIsBulkActionsOpen(false);
    };

    if (isBulkActionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBulkActionsOpen]);

  const handleBulkActionSelect = (action: string) => {
    setSelectedBulkAction(action);
    setIsBulkActionsOpen(false);
  };

  const handleClickCancel = () => {
    setSelectedBulkAction(null);
    setSelectedRows([]);
  };

  const handleClickSummarySave = () => {
    if (selectedBulkAction === 'hatvdashboard_bulk_route' && selectedRows.length > 0) {
      setIsRoutingModalOpen(true);
    } else if (selectedBulkAction === 'hatvdashboard_bulk_approval' && selectedRows.length > 0) {
      setIsBulkApproveModalOpen(true);
    }
  };

  useEffect(() => {
    if (!isActive) {
      setSelectedBulkAction(null);
      setSelectedRows([]);
      setIsBulkActionsOpen(false);
    }
  }, [isActive]);

  const ViewApprovalModalStyles = {
    minWidth: '472px',
    minHeight: '400px',
    overflow: 'visible',
  };

  return (
    <div className={styles.award_section}>
      <div className={styles.header_wrapper}>
        <div className={styles.option_container}>
          <div
            className={`${styles.bulk_actions_wrapper} ${selectedBulkAction ? styles.disabled : ''}`}
            ref={bulkActionsRef}
          >
            <div
              className={`${styles.bulk_actions_trigger} ${selectedBulkAction ? styles.disabled : ''}`}
              onClick={
                selectedBulkAction ? undefined : () => setIsBulkActionsOpen(!isBulkActionsOpen)
              }
            >
              <Icon
                name="bulk_list_icon"
                size={14}
                color="var(--text-text-primary, #D8D8D9)"
              />
              <span className={styles.bulk_actions_label}>
                {selectedBulkAction ? t(selectedBulkAction) : t('hatvdashboard_bulk_actions')}
              </span>
              <Icon
                name="dropdown_icon"
                size={14}
                className={`${styles.dropdown_arrow} ${isBulkActionsOpen ? styles.rotate : ''}`}
                color="var(--text-text-primary, #D8D8D9)"
              />
            </div>
            {isBulkActionsOpen &&
              createPortal(
                <div
                  className={styles.bulk_actions_menu}
                  ref={menuRef}
                  style={{
                    top: menuCoords.top,
                    left: menuCoords.left,
                  }}
                >
                  <div
                    className={`${styles.bulk_actions_item} ${selectedBulkAction === 'hatvdashboard_bulk_approval' ? styles.selected : ''}`}
                    onClick={() => handleBulkActionSelect('hatvdashboard_bulk_approval')}
                  >
                    {t('hatvdashboard_bulk_approval')}
                  </div>
                  <div
                    className={`${styles.bulk_actions_item} ${selectedBulkAction === 'hatvdashboard_bulk_route' ? styles.selected : ''}`}
                    onClick={() => handleBulkActionSelect('hatvdashboard_bulk_route')}
                  >
                    {t('hatvdashboard_bulk_route')}
                  </div>
                </div>,
                document.body
              )}
          </div>
        </div>
        <div className={`${styles.btn_wrapper} ${styles.right_buttons}`}>
          <div className={styles.time_selector}>
            <CalendarDropdown
              monthYearList={monthYearList}
              onselectionMonthYear={handleSelectedCode}
              selectedMonth={selectedYearMonth}
              isOnlyYear={false}
              isLoading={isInitialLoading}
              isError={isError}
            />
          </div>
        </div>
      </div>

      <div className={styles.award_bulk_section}>
        <HATVBulkActionBar
          selectedItems={selectedItems}
          selectedOptionKey={selectedOptionKey}
          selectedBulkAction={selectedBulkAction}
          onCancel={handleClickCancel}
          onSave={handleClickSummarySave}
        />

        <div className={styles.content_wrapper} data-testid="content_wrapper_testID">
          <div className={`content_inner ${styles.inner_wrapper}`}>
            <AwardApproval
              data={awardApprovalData}
              isAwardApprovalLoading={isInitialLoading || isAwardApprovalFetching}
              isError={isAwardApprovalError}
              setIsStatusUpdate={handleAPIStatus}
              selectedRows={selectedRows}
              onRowSelection={setSelectedRows}
              isBulkSelectionMode={!!selectedBulkAction}
              selectedBulkAction={selectedBulkAction}
            />
          </div>
        </div>
      </div>
      {isRoutingModalOpen && selectedRows.length > 0 && (
        <ViewRoutingModal
          isOpen={isRoutingModalOpen}
          onClose={() => setIsRoutingModalOpen(false)}
          selectedAbos={selectedRows}
          performanceYear={selectedYearMonth?.substring(0, 4) || ''}
          setIsStatusUpdate={(status) => {
            if (status === 'success') {
              handleStatusUpdate();
              setIsRoutingModalOpen(false);
              setSelectedBulkAction(null);
              setSelectedRows([]);
            }
          }}
        />
      )}
      {isBulkApproveModalOpen && selectedRows.length > 0 && (
        <Modal
          showDrawer={isBulkApproveModalOpen}
          onClose={() => setIsBulkApproveModalOpen(false)}
          customStyle={ViewApprovalModalStyles}
        >
          <HATVApprovalActionModal
            aboNumber=""
            affiliateCode=""
            aboHatvAwardApproverIdToApprove={null}
            aboHatvAwardApproverIdToDeny={null}
            approvedFor=""
            approvalType={HATV_APPROVAL_ACTION.APPROVE}
            onClose={() => setIsBulkApproveModalOpen(false)}
            selectedAbos={selectedRows}
            setIsStatusUpdate={(status) => {
              handleAPIStatus(status);
              if (status === 'success') {
                setIsBulkApproveModalOpen(false);
                setSelectedBulkAction(null);
                setSelectedRows([]);
              }
            }}
          />
        </Modal>
      )}
      {toastConfig && (
        <ToastMessage
          message={toastConfig.message}
          toastType={toastConfig.type}
          showToast={toastConfig.show}
          setShowToast={setShowToast}
          customStyles={{
            top: '64px',
          }}
          autoDismissal
        />
      )}
    </div>
  );
};

const useAwardApprovalData = ({ closingMonth }: { closingMonth: string }) => {
  const { t } = useTranslation();

  const shouldSkipApprovalQuery = !closingMonth;

  const {
    data: approvalData,
    isLoading: isInitialLoading,
    ...approvalQueryAttrs
  } = useGetMyAwardApprovalQuery(shouldSkipApprovalQuery ? skipToken : { closingMonth });

  const nativeIDs = useMemo(
    () => approvalData?.abos?.map((abo) => abo.accountManager?.userNativeId).filter(Boolean) ?? [],
    [approvalData]
  );

  const {
    data: userDetailsRes,
    isLoading: isUserListLoading,
    isError: isErrorInNativeIdUser,
  } = useGetUsersListByNativeIDsQuery({ nativeIDs }, { skip: nativeIDs.length === 0 });

  const userDetailsMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!Array.isArray(userDetailsRes?.body) || isErrorInNativeIdUser) {
      return map;
    }
    userDetailsRes?.body?.forEach((user) => {
      if (user.nativeId) map.set(user.nativeId, user.fullName);
    });
    return map;
  }, [userDetailsRes]);

  const tableRows = useMemo(() => {
    if (!approvalData?.abos) return [];

    return approvalData.abos.map<TableRowData>((abo: any) => {
      const status = abo.awardApproval.finalApprovalStatus;
      const statusName = t(`hatv_view_routing.routing_status.${status}`) || '-';
      const nativeId = abo.accountManager?.userNativeId;
      const managerName = userDetailsMap.get(nativeId ?? '') ?? nativeId ?? '-';

      return {
        ...abo,
        uplineDiamond: abo.uplineDiamond?.name,
        uplinePlatinum: abo.uplinePlatinum?.name,
        manager: managerName,
        status,
        statusName,
        kpis: [],
        roleIndicators: abo.hatvApprovalRoleIndicators,
        routingRoleIndicators: abo.hatvRoutingRoleIndicators,
      };
    });
  }, [approvalData, t, userDetailsMap]);

  return {
    ...approvalQueryAttrs,
    data: tableRows,
    isLoading: isInitialLoading || isUserListLoading,
  };
};

export default HATVAwardApprovalSection;
